// src/components/OPDSupportTrackingPage.js
import React, { useEffect, useState, useMemo } from "react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { Search, Filter, Download, X, ChevronDown, ChevronUp, ChevronsUpDown, Plus, Check, Calendar, Users } from 'lucide-react';

export default function OPDSupportTrackingPage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tracking'); // 'tracking' | 'provided'
  const [filters, setFilters] = useState({
    status: "",
    search: "",
    disabilityType: "",
    supportProvided: "",
    supportNeeded: "",
  });

  const [selectedSupport, setSelectedSupport] = useState({});
  const [extraSupport, setExtraSupport] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: "firstName", direction: "asc" });
  const itemsPerPage = 10;

  const matchesNeededWithReceived = (neededLabel, receivedList) => {
    const needle = (neededLabel || '').toString().trim().toLowerCase();
    if (!needle) return false;
    return (receivedList || []).some((r) =>
      r && r.toString().toLowerCase().includes(needle)
    );
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'tracking') {
      setFilters({
        status: "",
        search: "",
        disabilityType: "",
        supportProvided: "",
        supportNeeded: "",
      });
      setCurrentPage(1);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const currentUser = JSON.parse(localStorage.getItem("currentUser"));
      const opdEmail = currentUser?.email;

      if (!opdEmail) {
        alert("No logged-in OPD found.");
        setLoading(false);
        return;
      }

      // Get OPD profile to determine sub city
      const API_BASE_URL = 'https://aaapd.org.et/gallery_backend/api';
      let subCity = '';
      try {
        const profRes = await fetch(`${API_BASE_URL}/get_opd_by_email.php?email=${encodeURIComponent(opdEmail)}`);
        const profData = await profRes.json();
        if (profData?.status === 'success' && profData?.data?.sub_city) {
          subCity = profData.data.sub_city;
        }
      } catch (_) {}

      // Build filters from current UI filters if needed (basic search/disability)
      const params = new URLSearchParams({
        ...(filters.search && { search: filters.search }),
        ...(filters.disabilityType && { disabilityType: filters.disabilityType }),
      });

      const resp = await fetch(`${API_BASE_URL}/getAllMembers.php?${params.toString()}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();

      const norm = (v) => (v ?? '').toString().trim().toLowerCase();
      const subCityNorm = norm(subCity);
      const raw = Array.isArray(data) ? data.filter(m => !subCity || norm(m.subCity) === subCityNorm) : [];

      const normalized = raw.map((m) => {
        const fullName = `${m.firstName ?? ''} ${m.middleName ? m.middleName + ' ' : ''}${m.lastName ?? ''}`.trim();
        const rec = Array.isArray(m.supportReceived)
          ? m.supportReceived
          : (m.supportReceived ? m.supportReceived.split(',').map(s => s.trim()).filter(Boolean) : []);
        const needed = (m.supportNeeded || '').split(',').map(s => s.trim()).filter(Boolean);
        let derivedStatus = 'Pending';
        if (needed.length > 0) {
          if (needed.every((s) => matchesNeededWithReceived(s, rec))) {
            derivedStatus = 'Completed';
          } else if (rec.length > 0) {
            derivedStatus = 'In Progress';
          }
        } else if (rec.length > 0) {
          derivedStatus = 'In Progress';
        }
        const lastSupportGivenAt = m.lastSupportGivenAt || m.last_support_given_at || null;
        return {
          ...m,
          fullName,
          supportReceived: rec,
          status: m.status || derivedStatus,
          lastSupportGivenAt,
        };
      });

      // Merge any locally stored lastSupportGivenAt if backend doesn't provide it
      const localDates = JSON.parse(localStorage.getItem('supportDates') || '{}');
      const merged = normalized.map(m => {
        if (!m.lastSupportGivenAt && localDates[m.id]) {
          return { ...m, lastSupportGivenAt: localDates[m.id] };
        }
        return m;
      });

      setMembers(merged);
    } catch (error) {
      console.error("Failed to fetch members:", error);
    }
    setLoading(false);
  };

  const handleAddSupport = async (memberId) => {
    const category = (selectedSupport[memberId] || '').trim();
    const description = (extraSupport[memberId] || '').trim();
    if (!category) return; // must choose a category
    const support = description ? `${category}: ${description}` : category;

    try {
      const member = members.find((m) => m.id === memberId);
      const updatedSupport = [...(member.supportReceived || []), support];

      const neededSupports = (member.supportNeeded || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      let newStatus = "Pending";
      if (neededSupports.length > 0) {
        if (neededSupports.every((s) => matchesNeededWithReceived(s, updatedSupport))) {
          newStatus = "Completed";
        } else if (updatedSupport.length > 0) {
          newStatus = "In Progress";
        }
      } else if (updatedSupport.length > 0) {
        newStatus = "In Progress";
      }

      // Persist to backend (also store the date given) using LOCAL time (avoid UTC day shift)
      try {
        const API_BASE_URL = 'https://aaapd.org.et/gallery_backend/api';
        const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
        const now = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        const localDateTime = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
        await fetch(`${API_BASE_URL}/updateMember.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: memberId,
            supportReceived: updatedSupport.join(', '),
            last_support_given_at: localDateTime,
            requestEmail: user.email || ''
          }),
        });
      } catch (_) {}

      const nowLocal = new Date();
      const pad2 = (n) => String(n).padStart(2, '0');
      const nowLocalStr = `${nowLocal.getFullYear()}-${pad2(nowLocal.getMonth()+1)}-${pad2(nowLocal.getDate())} ${pad2(nowLocal.getHours())}:${pad2(nowLocal.getMinutes())}:${pad2(nowLocal.getSeconds())}`;
      setMembers(
        members.map((m) => (m.id === memberId ? { ...m, supportReceived: updatedSupport, status: newStatus, lastSupportGivenAt: nowLocalStr } : m))
      );
      // Save to localStorage as a fallback persistence
      try {
        const map = JSON.parse(localStorage.getItem('supportDates') || '{}');
        map[memberId] = nowLocalStr;
        localStorage.setItem('supportDates', JSON.stringify(map));
      } catch {}
      setSelectedSupport((prev) => ({ ...prev, [memberId]: "" }));
      setExtraSupport((prev) => ({ ...prev, [memberId]: "" }));
    } catch (error) {
      console.error("Failed to add support:", error);
    }
  };

  const handleRemoveSupport = async (memberId, support) => {
    try {
      const member = members.find((m) => m.id === memberId);
      const updatedSupport = member.supportReceived.filter((s) => s !== support);

      const neededSupports = (member.supportNeeded || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      let newStatus = "Pending";
      if (neededSupports.every((s) => updatedSupport.includes(s))) {
        newStatus = "Completed";
      } else if (updatedSupport.length > 0) {
        newStatus = "In Progress";
      }

      // Persist to backend
      try {
        const API_BASE_URL = 'https://aaapd.org.et/gallery_backend/api';
        const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
        await fetch(`${API_BASE_URL}/updateMember.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: memberId,
            supportReceived: updatedSupport.join(', '),
            requestEmail: user.email || ''
          }),
        });
      } catch (_) {}

      setMembers(
        members.map((m) => (m.id === memberId ? { ...m, supportReceived: updatedSupport, status: newStatus } : m))
      );
    } catch (error) {
      console.error("Failed to remove support:", error);
    }
  };

  const onFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  // Enhanced filtering
  const filteredMembers = useMemo(() => {
    return members.filter((m) => {
      const isApproved = (m.aaapd_status || '').toString().toLowerCase() === 'approved';
      if (!isApproved) return false;
      const matchesStatus = filters.status ? m.status === filters.status : true;
      const matchesDisability = filters.disabilityType ? m.disabilityType === filters.disabilityType : true;
      
      const matchesSearch = filters.search
        ? (m.fullName || "").toString().toLowerCase().includes(filters.search.toLowerCase()) ||
          (m.supportNeeded || "").toString().toLowerCase().includes(filters.search.toLowerCase()) ||
          (m.idNumber || "").toString().toLowerCase().includes(filters.search.toLowerCase())
        : true;
      const matchesSupportProvided = filters.supportProvided
        ? (m.supportReceived || []).some((s) => {
            if (!s) return false;
            const [cat] = s.split(':');
            return (cat || '').trim().toLowerCase() === filters.supportProvided.toLowerCase();
          })
        : true;
      const matchesSupportNeeded = filters.supportNeeded
        ? (m.supportNeeded || '').split(',').map(s => s.trim().toLowerCase()).includes(filters.supportNeeded.toLowerCase())
        : true;

      return matchesStatus && matchesDisability && matchesSearch && matchesSupportProvided && matchesSupportNeeded;
    });
  }, [members, filters]);

  // Sorting
  const sortedMembers = useMemo(() => {
    const sorted = [...filteredMembers];
    if (sortConfig.key) {
      sorted.sort((a, b) => {
        let aVal = a[sortConfig.key] ?? "";
        let bVal = b[sortConfig.key] ?? "";
        
        if (sortConfig.key === "createdAt") {
          aVal = new Date(aVal);
          bVal = new Date(bVal);
        }
        
        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sorted;
  }, [filteredMembers, sortConfig]);

  const paginatedMembers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedMembers.slice(start, start + itemsPerPage);
  }, [sortedMembers, currentPage]);

  const totalPages = Math.ceil(sortedMembers.length / itemsPerPage);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const allDisabilityTypes = [
    "Visual impairment",
    "Hearing impairment",
    "Physical/Mobility impairment",
    "Intellectual disability",
    "Mental health condition",
    "Chronic illness",
    "Speech impairment",
    "Autism/Neurodivergence",
    "Other",
  ];
  const SUPPORT_CATEGORIES = [
    "Materials",
    "Training",
    "Job",
    "Education",
    "Healthcare",
    "Financial Support",
    "Holiday & Welfare Support",
    "Other",
  ];
  const availableNeededSupports = useMemo(() => {
    const set = new Set();
    members.forEach(m => {
      (m.supportNeeded || '').split(',').map(s => s.trim()).filter(Boolean).forEach(s => set.add(s));
    });
    return Array.from(set);
  }, [members]);

  const statusColor = (status) => {
    if (status === "Completed") return "bg-green-500";
    if (status === "In Progress") return "bg-orange-500";
    return "bg-red-500";
  };

  const exportCSV = () => {
    const headers = ["Name", "Disability", "Support Needed", "Support Received", "Status", "Date Requested"];
    const rows = sortedMembers.map((m) => [
      m.fullName,
      m.disabilityType || "",
      m.supportNeeded || "",
      (m.supportReceived || []).join(", "),
      m.status,
      new Date(m.createdAt).toLocaleDateString(),
    ]);

    let csvContent = "data:text/csv;charset=utf-8," 
      + [headers, ...rows].map(e => e.join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `opd-support-tracking-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Get current user info for display
  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");

  return (
    <div className="bg-gray-100 min-h-screen p-4 md:p-8">
      {/* Header */}
      <header className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Users className="w-8 h-8 text-teal-600" />
          <h1 className="text-4xl font-bold text-gray-800">OPD Support Tracking</h1>
        </div>
        <p className="text-gray-500">Track and manage support requests for your OPD members.</p>
        {currentUser.name && (
          <p className="text-sm text-teal-600 mt-1">Managing: {currentUser.name}</p>
        )}
      </header>

      {/* Tabs */}
      <div className="flex gap-3 mb-4">
        <button
          type="button"
          className={`px-4 py-2 rounded-lg font-medium transition-all ${activeTab === 'tracking' ? 'bg-teal-600 text-white shadow' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          onClick={() => handleTabChange('tracking')}
        >
          Tracking
        </button>
        <button
          type="button"
          className={`px-4 py-2 rounded-lg font-medium transition-all ${activeTab === 'provided' ? 'bg-teal-600 text-white shadow' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          onClick={() => handleTabChange('provided')}
        >
          Provided Supports
        </button>
      </div>

      {/* Control Panel */}
      {activeTab === 'tracking' && (
      <div className="bg-white p-6 rounded-xl shadow-md mb-8">
        <div className="mb-4 flex justify-end">
          <span className="px-3 py-1 rounded-full bg-teal-50 text-teal-700 font-semibold text-sm">
            {sortedMembers.length} result{sortedMembers.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="search"
              placeholder="Search by name, ID, or support needed..."
              value={filters.search}
              onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 transition"
            />
          </div>

          {/* Status Filter */}
          <select
            name="status"
            value={filters.status}
            onChange={onFilterChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 transition bg-white"
          >
            <option value="">All Status</option>
            <option value="Pending">Pending</option>
            <option value="In Progress">In Progress</option>
            <option value="Completed">Completed</option>
          </select>

          {/* Disability Type Filter */}
          <select
            name="disabilityType"
            value={filters.disabilityType}
            onChange={onFilterChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 transition bg-white"
          >
            <option value="">All Disabilities</option>
            {allDisabilityTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>

          {/* Support Needed Filter */}
          <select
            name="supportNeeded"
            value={filters.supportNeeded}
            onChange={onFilterChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 transition bg-white"
          >
            <option value="">All Support Needed</option>
            {availableNeededSupports.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>

          {/* Export Button */}
          <div className="flex items-center justify-end">
            <button
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors shadow-sm"
              onClick={exportCSV}
            >
              <Download className="w-5 h-5" />
              Export CSV
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Support Tracking Table */}
      {activeTab === 'tracking' && (
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {loading ? (
          <p className="text-center text-gray-500 py-16">Loading support data...</p>
        ) : sortedMembers.length === 0 ? (
          <p className="text-center text-gray-500 py-16">No members found matching your criteria.</p>
        ) : (
          <>
            {/* Table for Medium screens and up */}
            <div className="overflow-x-auto hidden md:block">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3 px-4 text-left font-semibold text-gray-600 uppercase tracking-wider w-12">#</th>
                    {[
                      { key: 'fullName', label: 'Name' },
                      { key: 'disabilityType', label: 'Disability' },
                      { key: 'supportNeeded', label: 'Support Needed' },
                      { key: 'supportReceived', label: 'Support Provided' },
                      { key: 'status', label: 'Status' },
                      { key: 'createdAt', label: 'Date Requested' },
                    ].map(col => (
                      <th 
                        key={col.key} 
                        onClick={col.key !== 'supportNeeded' && col.key !== 'supportReceived' ? () => requestSort(col.key) : undefined}
                        className={`py-3 px-4 text-left font-semibold text-gray-600 uppercase tracking-wider ${
                          col.key !== 'supportNeeded' && col.key !== 'supportReceived' 
                            ? 'cursor-pointer hover:bg-gray-100' 
                            : ''
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {col.label}
                          {(col.key !== 'supportNeeded' && col.key !== 'supportReceived') && sortConfig.key === col.key ? (
                            sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                          ) : (col.key !== 'supportNeeded' && col.key !== 'supportReceived') ? <ChevronsUpDown className="w-4 h-4 text-gray-400" /> : null}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginatedMembers.map((m, index) => {
                    const neededSupports = (m.supportNeeded || "")
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean);
                    
                    return (
                      <tr key={m.id} className="hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-4 whitespace-nowrap text-gray-600">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                        <td className="py-3 px-4 text-gray-700 font-medium">{m.fullName}</td>
                        <td className="py-3 px-4 text-gray-600">{m.disabilityType}</td>
                        <td className="py-3 px-4">
                          <div className="space-y-1">
                            {neededSupports.length > 0 ? (
                              neededSupports.map((item, index) => {
                                const isReceived = matchesNeededWithReceived(item, m.supportReceived);
                                return (
                                  <div
                                    key={index}
                                    className={`flex items-center gap-2 text-sm ${
                                      isReceived ? "text-green-600" : "text-red-600"
                                    }`}
                                  >
                                    {isReceived ? (
                                      <Check className="w-4 h-4" />
                                    ) : (
                                      <X className="w-4 h-4" />
                                    )}
                                    <span className={isReceived ? "" : "font-semibold"}>
                                      {item}
                                    </span>
                                  </div>
                                );
                              })
                            ) : (
                              <span className="text-gray-500 text-sm italic">Not specified</span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="space-y-2">
                            {m.supportReceived.map((s, i) => (
                              <div key={i} className="flex justify-between items-center bg-blue-100 p-2 rounded-lg text-sm">
                                <span className="text-blue-800">
                                  {s}
                                  {m.lastSupportGivenAt ? (
                                    <span className="text-blue-700/80 ml-1">
                                      ({new Date(m.lastSupportGivenAt).toLocaleDateString()})
                                    </span>
                                  ) : null}
                                </span>
                                <button
                                  onClick={() => handleRemoveSupport(m.id, s)}
                                  className="text-red-500 hover:text-red-700 ml-2"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                            <div className="flex flex-col gap-2">
                              <div className="flex gap-2">
                                <select
                                  value={selectedSupport[m.id] || ""}
                                  onChange={(e) => setSelectedSupport((prev) => ({ ...prev, [m.id]: e.target.value }))}
                                  className="border border-gray-300 rounded-lg px-2 py-1 text-sm flex-1 focus:outline-none focus:ring-2 focus:ring-teal-500"
                                >
                                  <option value="">Select support type</option>
                                  {SUPPORT_CATEGORIES.map((cat) => (
                                    <option key={cat} value={cat}>{cat}</option>
                                  ))}
                                </select>
                                <input
                                  type="text"
                                  value={extraSupport[m.id] || ""}
                                  onChange={(e) => setExtraSupport((prev) => ({ ...prev, [m.id]: e.target.value }))}
                                  placeholder="Description (optional)"
                                  className="border border-gray-300 rounded-lg px-2 py-1 text-sm flex-1 focus:outline-none focus:ring-2 focus:ring-teal-500"
                                />
                                <button
                                  onClick={() => handleAddSupport(m.id)}
                                  className="flex items-center gap-1 bg-teal-600 text-white px-3 py-1 rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
                                >
                                  <Plus className="w-4 h-4" />
                                  Add
                                </button>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <span className={`w-3 h-3 rounded-full ${statusColor(m.status)}`}></span>
                            <span className="text-sm font-medium">{m.status}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-gray-600 text-sm">
                          {new Date(m.createdAt).toLocaleDateString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Cards for Small screens */}
            <div className="block md:hidden">
              <div className="divide-y divide-gray-200">
                {paginatedMembers.map((m) => {
                  const neededSupports = (m.supportNeeded || "")
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean);

                  return (
                    <div key={m.id} className="p-4">
                      <div className="space-y-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-bold text-gray-800">{m.fullName}</p>
                            <p className="text-sm text-gray-600">{m.disabilityType}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${statusColor(m.status)}`}></span>
                            <span className="text-sm text-gray-600">{m.status}</span>
                          </div>
                        </div>
                        
                        <div>
                          <p className="font-medium text-gray-700 mb-2">Support Needed:</p>
                          <div className="space-y-1">
                            {neededSupports.length > 0 ? (
                              neededSupports.map((item, index) => {
                                const isReceived = m.supportReceived.includes(item);
                                return (
                                  <div
                                    key={index}
                                    className={`flex items-center gap-2 text-sm ${
                                      isReceived ? "text-green-600" : "text-red-600"
                                    }`}
                                  >
                                    {isReceived ? (
                                      <Check className="w-4 h-4" />
                                    ) : (
                                      <X className="w-4 h-4" />
                                    )}
                                    <span>{item}</span>
                                  </div>
                                );
                              })
                            ) : (
                              <span className="text-gray-500 text-sm italic">Not specified</span>
                            )}
                          </div>
                        </div>

                        {m.supportReceived.length > 0 && (
                          <div>
                            <p className="font-medium text-gray-700 mb-2">Support Provided:</p>
                            <div className="space-y-1">
                              {m.supportReceived.map((s, i) => (
                                <div key={i} className="bg-blue-100 p-2 rounded text-sm">
                                  <span className="text-blue-800">
                                    {s}
                                    {m.lastSupportGivenAt ? (
                                      <span className="text-blue-700/80 ml-1">
                                        ({new Date(m.lastSupportGivenAt).toLocaleDateString()})
                                      </span>
                                    ) : null}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <p className="text-xs text-gray-500">
                          Requested: {new Date(m.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Pagination */}
            <div className="flex flex-col md:flex-row justify-between items-center p-4 border-t border-gray-200 gap-4">
              <span className="text-sm text-gray-600">
                Showing {paginatedMembers.length} of {sortedMembers.length} members
              </span>
              <div className="flex items-center gap-2">
                <button
                  className="px-3 py-1 border rounded-lg bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Prev
                </button>
                <span className="text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  className="px-3 py-1 border rounded-lg bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      )}

      {/* Provided Supports Tab */}
      {activeTab === 'provided' && (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {loading ? (
            <p className="text-center text-gray-500 py-16">Loading support data...</p>
          ) : (
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Provided Supports</h3>
                <span className="px-3 py-1 rounded-full bg-teal-50 text-teal-700 font-semibold text-sm">
                  {filteredMembers.filter(m => (m.supportReceived || []).length > 0).length} result{filteredMembers.filter(m => (m.supportReceived || []).length > 0).length !== 1 ? 's' : ''}
                </span>
              </div>
              {/* Filters inside Provided Supports */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="search"
                    placeholder="Search by name or support..."
                    value={filters.search}
                    onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                    className="w-full md:w-auto pl-10 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 transition"
                  />
                </div>
                <select
                  name="disabilityType"
                  value={filters.disabilityType}
                  onChange={onFilterChange}
                  className="w-full md:w-auto border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 transition bg-white"
                >
                  <option value="">All Disabilities</option>
                  {allDisabilityTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                {/* Support Needed (compact) */}
                <select
                  name="supportNeeded"
                  value={filters.supportNeeded}
                  onChange={onFilterChange}
                  className="w-full md:w-auto border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 transition bg-white"
                >
                  <option value="">All Support Needed</option>
                  {availableNeededSupports.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <select
                  name="supportProvided"
                  value={filters.supportProvided}
                  onChange={onFilterChange}
                  className="w-full md:w-auto border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 transition bg-white"
                >
                  <option value="">All Provided Supports</option>
                  {SUPPORT_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="py-3 px-4 text-left font-semibold text-gray-600 uppercase tracking-wider w-12">#</th>
                      <th className="py-3 px-4 text-left font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                      <th className="py-3 px-4 text-left font-semibold text-gray-600 uppercase tracking-wider">Disability</th>
                      <th className="py-3 px-4 text-left font-semibold text-gray-600 uppercase tracking-wider">Supports Provided</th>
                      <th className="py-3 px-4 text-left font-semibold text-gray-600 uppercase tracking-wider">Date Requested</th>
                      <th className="py-3 px-4 text-left font-semibold text-gray-600 uppercase tracking-wider">Date Given</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredMembers
                      .filter(m => (m.supportReceived || []).length > 0)
                      .map((m, index) => (
                        <tr key={m.id} className="hover:bg-gray-50 transition-colors">
                          <td className="py-3 px-4 whitespace-nowrap text-gray-600">{index + 1}</td>
                          <td className="py-3 px-4 text-gray-700 font-medium">{m.fullName}</td>
                          <td className="py-3 px-4 text-gray-600">{m.disabilityType || '-'}</td>
                          <td className="py-3 px-4">
                            <div className="flex flex-wrap gap-2">
                              {(m.supportReceived || []).map((s, i) => (
                                <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">{s}</span>
                              ))}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-gray-600 text-sm">{m.createdAt ? new Date(m.createdAt).toLocaleDateString() : '-'}</td>
                          <td className="py-3 px-4 text-gray-600 text-sm">{m.lastSupportGivenAt ? new Date(m.lastSupportGivenAt).toLocaleDateString() : '-'}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
